import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { LoggingService } from '@treasury/core/logging';
import { FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmBaseComponent } from '@treasury/presentation';
import { closeIcon, informationIcon } from '@treasury/presentation/assets/icons';
import { LocalStorageService } from '@treasury/utils/services';
import { MediaService } from '@treasury/utils/services/media-service';
import { css, html, nothing, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-tooltip-rich';

export const tagName = 'tm-check-capture-manual';

/**
 * Manual check capture component using vanilla JavaScript camera APIs
 * @fires TmCheckCaptureManual#image-capture-failed - Event fired when the image capture fails.
 */
@customElement(tagName)
export class TmCheckCaptureManual extends TmBaseComponent {
    @property({ type: String, reflect: true })
    public checkSide: 'front' | 'back' = 'front';

    @property({ attribute: false })
    public photo?: string | null;

    @property({ type: Boolean, reflect: true })
    public captureOnStart = false;

    @InjectProperty()
    declare private mediaService: MediaService;

    @InjectProperty()
    declare private featureFlagService: FeatureFlagService;

    @InjectProperty()
    declare private readonly logService: LoggingService;

    @InjectProperty()
    declare private readonly localStorageService: LocalStorageService;

    @state()
    private isCapturing = false;

    @state()
    private showManualCaptureButton = false;

    @state()
    private cameraError: string | undefined = undefined;

    private videoElement?: HTMLVideoElement;

    private canvasElement?: HTMLCanvasElement;

    private stream?: MediaStream;

    private cameraContainer?: HTMLElement;

    public connectedCallback(): void {
        super.connectedCallback();
        this.setupCameraContainer();
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.stopCapture();
    }

    public stopCapture() {
        this.logService.logInfo('tm-check-capture-manual stopping capture', {
            capture_step: 'Stop',
            check_side: this.checkSide,
        });

        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = undefined;
        }

        if (this.videoElement && this.videoElement.srcObject) {
            this.videoElement.pause();
            this.videoElement.srcObject = undefined as unknown as MediaStream;
        }

        this.isCapturing = false;
        this.cameraError = undefined;
        this.removeOverlay();
    }

    public async startCapture() {
        this.isCapturing = true;
        this.cameraError = undefined;

        try {
            this.logService.logInfo('tm-check-capture-manual starting capture', {
                capture_step: 'Start',
                check_side: this.checkSide,
            });

            // Request camera permission and access
            const isCameraAllowed = await this.getCameraPermission();

            if (!isCameraAllowed) {
                this.logService.logError('tm-check-capture-manual camera permission denied');
                this.handleCaptureError(
                    'Camera permission denied. Please allow camera access to capture checks.'
                );
                return;
            }

            // Get video stream
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment', // Use back camera on mobile devices
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            });

            this.setupVideoElement();
            this.addOverlay();
        } catch (error) {
            this.logService.logError('tm-check-capture-manual start capture error');
            this.handleCaptureError(
                error instanceof Error ? error.message : 'Failed to access camera'
            );
        }
    }

    public captureFrame() {
        if (!this.videoElement || !this.stream) {
            this.handleCaptureError('Camera not initialized');
            return;
        }

        try {
            this.logService.logInfo('tm-check-capture-manual capturing frame', {
                capture_step: 'Capture',
                check_side: this.checkSide,
            });

            // Create canvas to capture the frame
            const canvas = this.getCanvas();
            const context = canvas.getContext('2d');

            if (!context) {
                this.handleCaptureError('Failed to get canvas context');
                return;
            }

            // Set canvas dimensions to match video
            canvas.width = this.videoElement.videoWidth;
            canvas.height = this.videoElement.videoHeight;

            // Draw the video frame to canvas
            context.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height);

            // Convert to base64 image data
            const imageData = canvas.toDataURL('image/jpeg', 0.9);

            // Generate preview (rotate 180 degrees for manual captures)
            this.generatePreviewImage(imageData);

            // Dispatch success event
            this.dispatchEvent(
                new CustomEvent('image-uploaded', {
                    detail: {
                        checkSide: this.checkSide,
                        image: imageData,
                    },
                })
            );

            this.stopCapture();
        } catch {
            this.logService.logError('tm-check-capture-manual capture frame error');
            this.handleCaptureError('Failed to capture image');
        }
    }

    protected render() {
        return html`
            <button
                class="output relative text-center flex items-center justify-between grow align-top"
                @click=${() => {
                    if (!this.captureOnStart && !this.isCapturing) {
                        this.startCapture();
                    }
                }}
            >
                ${this.renderPhoto()} ${this.renderCameraContainer()}
            </button>
            ${this.renderManualButton()} ${this.renderError()}
        `;
    }

    protected updated(_changedProperties: Map<PropertyKey, unknown>): void {
        if (_changedProperties.has('captureOnStart')) {
            if (this.captureOnStart) {
                this.startCapture();
            }
        }
    }

    private async getCameraPermission(): Promise<boolean> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            const hasCamera = stream.active;
            stream.getTracks().forEach(track => track.stop());
            return hasCamera;
        } catch {
            return false;
        }
    }

    private setupCameraContainer() {
        // Create a container for the camera view that will be positioned over the component
        this.cameraContainer = document.createElement('div');
        this.cameraContainer.setAttribute('id', 'manualCameraContainer');
        this.cameraContainer.setAttribute(
            'style',
            'position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 100; background-color: black; display: none;'
        );
        document.body.appendChild(this.cameraContainer);
    }

    private setupVideoElement() {
        if (!this.stream || !this.cameraContainer) {
            return;
        }

        // Create video element
        this.videoElement = document.createElement('video');
        this.videoElement.setAttribute('autoplay', 'true');
        this.videoElement.setAttribute('muted', 'true');
        this.videoElement.setAttribute('playsinline', 'true');
        this.videoElement.style.cssText = `
            width: 100%;
            height: 100%;
            object-fit: cover;
        `;

        // Set the stream as video source
        this.videoElement.srcObject = this.stream;

        // Clear container and add video
        this.cameraContainer.innerHTML = '';
        this.cameraContainer.appendChild(this.videoElement);
        this.cameraContainer.style.display = 'block';

        this.videoElement.play().catch(() => {
            this.logService.logError('tm-check-capture-manual video play error');
            this.handleCaptureError('Failed to start camera preview');
        });
    }

    private getCanvas(): HTMLCanvasElement {
        if (!this.canvasElement) {
            this.canvasElement = document.createElement('canvas');
        }
        return this.canvasElement;
    }

    private handleCaptureError(message: string) {
        this.cameraError = message;
        this.isCapturing = false;
        this.stopCapture();

        this.dispatchEvent(
            new CustomEvent('image-capture-failed', {
                detail: {
                    response: {
                        status: 'FAILURE',
                        warnings: {
                            status: 'error',
                            code: 'MANUAL_CAPTURE_ERROR',
                            key: 'MANUAL_CAPTURE_ERROR',
                        },
                        mode: 'MANUAL',
                        docType: this.checkSide === 'front' ? 'CHECK_FRONT' : 'CHECK_BACK',
                        ClientActivityDataLog: {},
                        failedImage: '',
                        imageData: '',
                        mibData: {},
                    },
                },
            })
        );
    }

    private removeOverlay() {
        const overlayControls = document.querySelector('#manualCameraOverlayControls');
        overlayControls?.remove();

        if (this.cameraContainer) {
            this.cameraContainer.style.display = 'none';
        }
    }

    private async generatePreviewImage(imageData: string) {
        this.photo = imageData;
    }

    private close() {
        this.stopCapture();
        this.dispatchEvent(new CustomEvent('close'));
    }

    private overlayExists() {
        return document.querySelector('#manualCameraOverlayControls');
    }

    private addOverlay() {
        if (this.overlayExists() || !this.cameraContainer) {
            return;
        }

        const overlayContainer = document.createElement('div');
        overlayContainer.setAttribute('id', 'manualCameraOverlayControls');
        overlayContainer.setAttribute(
            'style',
            'position: fixed; right: 0px; top: 0px; left: 0px; bottom: 0px; z-index: 101; background-color: transparent; width: 100vw; height: 100vh;'
        );

        const overlayControls = this.renderOverlayControls();
        render(overlayControls, overlayContainer);
        this.cameraContainer.appendChild(overlayContainer);
    }

    private renderManualButton() {
        if (!this.showManualCaptureButton) {
            return nothing;
        }

        return html`<div class="fixed inset-0 bg-black z-40">
            <tm-button
                id="manual-capture-button"
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                @click=${() => {
                    this.startCapture();
                    this.showManualCaptureButton = false;
                }}
                primary
                small
            >
                Start Manual Capture
                ${this.isCapturing ? html`<tm-loader class="ml-2" small></tm-loader>` : nothing}
            </tm-button>
        </div>`;
    }

    private renderError() {
        if (!this.cameraError) {
            return nothing;
        }

        return html`
            <div
                class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
                <div class="bg-white rounded-lg p-6 max-w-md">
                    <h3 class="text-lg font-medium text-red-600 mb-2">Camera Error</h3>
                    <p class="text-gray-700 mb-4">${this.cameraError}</p>
                    <tm-button
                        @click=${() => {
                            this.cameraError = undefined;
                        }}
                        primary
                        small
                    >
                        OK
                    </tm-button>
                </div>
            </div>
        `;
    }

    private renderBackTipWarning() {
        if (this.checkSide !== 'back') {
            return nothing;
        }
        return html`<div
            class="flex justify-center items-center mt-3 mb-3 p-2 text-sm border rounded-lg border-[--content-attention]"
        >
            <div>
                <span class="font-bold">"For Mobile Deposit Only"</span> or the Restricted
                Endorsement required by your financial institution must be written clearly below
                your signature.
            </div>
        </div>`;
    }

    private renderHintTooltip() {
        return html`<div
            class="capture-hint-header flex flex-col items-left pb-1 font-medium text-[--header-text-color] border-[--border-color]"
        >
            <div>
                <span class="text-lg">Capture check ${this.checkSide}</span>
                <div class="font-normal text-sm">
                    Place check on a flat, well-lit, dark surface and tap the capture button when
                    ready.
                    <br /><br />
                    Make sure the check is clearly visible within the camera view and well-lit for
                    best results.
                </div>
            </div>
            ${this.renderBackTipWarning()}
            <div class="flex justify-end items-right pt-2">
                <img
                    class="w-1/3"
                    src="/pwa/assets/images/graphic-check-orientation-${this.checkSide}.png"
                    alt="Check ${this.checkSide} sample"
                />
            </div>
        </div>`;
    }

    private renderCaptureButton() {
        return html`<button
            id="manualCaptureButton"
            class="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-white border-4 border-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg"
            @click=${() => this.captureFrame()}
            ?disabled=${!this.isCapturing}
        >
            <div class="bg-red-500 rounded-full w-12 h-12"></div>
        </button>`;
    }

    private renderOverlayControls() {
        const checkSideText = this.checkSide === 'front' ? 'Front' : 'Back';

        return html`
            <div
                id="top-controls"
                class="relative text-center flex items-center justify-between p-2 w-screen landscape:-mt-3 pt-[--safe-area-top]"
            >
                <button class="close-button mt-2" @click=${() => this.close()}>
                    <tm-icon color="#ccc" .icon=${closeIcon}></tm-icon>
                </button>
                <div class="check-side-label text-gray-300 text-xl">${checkSideText} of Check</div>
                <tm-tooltip-rich direction="bottom-left" isClickable>
                    <tm-icon class="mt-2" color="#ccc" .icon=${informationIcon}></tm-icon>

                    <style>
                        .tooltip-content {
                            width: 328px;
                            margin-top: -7px;
                            color: var(--primary-text-color);
                        }
                    </style>
                    <div class="tooltip-content" slot="content">${this.renderHintTooltip()}</div>
                </tm-tooltip-rich>
            </div>

            <!-- Guide markers overlay -->
            <div class="guide-overlay absolute inset-0 pointer-events-none z-50">
                <div
                    class="guide-frame absolute top-1/2 left-1/2 w-4/5 h-4/5 border-2 rounded-xl max-w-sm max-h-screen"
                    style="
                    transform: translate(-50%, -50%);
                    border-color: rgba(255, 255, 255, 0.8);
                    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
                    max-height: 600px;
                "
                >
                    <div class="guide-corners relative w-full h-full">
                        <div
                            class="corner corner-tl absolute w-5 h-5 border-3 border-white rounded-tl-xl"
                            style="
                            top: -3px;
                            left: -3px;
                            border-right: none;
                            border-bottom: none;
                        "
                        ></div>
                        <div
                            class="corner corner-tr absolute w-5 h-5 border-3 border-white rounded-tr-xl"
                            style="
                            top: -3px;
                            right: -3px;
                            border-left: none;
                            border-bottom: none;
                        "
                        ></div>
                        <div
                            class="corner corner-bl absolute w-5 h-5 border-3 border-white rounded-bl-xl"
                            style="
                            bottom: -3px;
                            left: -3px;
                            border-right: none;
                            border-top: none;
                        "
                        ></div>
                        <div
                            class="corner corner-br absolute w-5 h-5 border-3 border-white rounded-br-xl"
                            style="
                            bottom: -3px;
                            right: -3px;
                            border-left: none;
                            border-top: none;
                        "
                        ></div>
                    </div>
                </div>
            </div>

            ${this.renderCaptureButton()}
        `;
    }

    private renderCameraContainer() {
        // This is just a placeholder div - the actual camera will be rendered in the body-level container
        return nothing;
    }

    private renderPhoto() {
        if (this.photo) {
            return html`
                <img
                    class="photo border shadow"
                    src=${this.photo}
                    alt="Captured ${this.checkSide} image"
                />
                ${this.renderCaptureLoading()}
            `;
        }
        return html` <div class="empty-photo border shadow">${this.renderCaptureLoading()}</div>`;
    }

    private renderCaptureLoading() {
        if (!this.isCapturing) {
            return nothing;
        }
        return this.captureOnStart
            ? html`<tm-blocking-loader fullscreen></tm-blocking-loader>`
            : html`<tm-loader class="absolute top-[-55%] left-[40%]" card></tm-loader>`;
    }

    public static get styles() {
        return [
            css`
                .output {
                    width: 140px;
                    --tm-loader-color: white;
                }

                .photo,
                .empty-photo {
                    width: 140px;
                }
                .empty-photo {
                    min-height: 140px;
                }

                :host([captureOnStart]),
                :host([captureOnStart]) .output,
                :host([captureOnStart]) .photo {
                    width: 100%;
                    height: 100%;
                    flex-grow: 1;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmCheckCaptureManual;
    }
}
